// ==========================================================================
// lib/tree.ts — the family tree: data and layout (FF-057, 10 Oct 2026)
//   James: "a really cool interactive way to build out a family tree with
//   lines and boxes and relations … a tree or a box and line style".
//   Layout: generations from the parent links (partners share a row), couples
//   kept side by side, children ordered under their parents, then a few passes
//   to centre parents over children and children under parents.
// ==========================================================================
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "./supabase";
import { useHouseholdId, usePeople } from "./household";

export type TreePerson = {
  id: string; person_id: string | null; given_name: string; family_name: string | null; maiden_name: string | null; nickname: string | null;
  born_on: string | null; born_year: number | null; died_on: string | null; died_year: number | null; birthplace: string | null; note: string | null;
  branch_colour: number | null; // 1..8, only matters for someone with no parents in the tree (038)
};
export type PartnerStatus = "married" | "partners" | "separated" | "divorced" | "widowed";
export type TreeLink = { id: string; kind: "parent" | "partner"; from_id: string; to_id: string; status: PartnerStatus | null; since_year: number | null };
export type TreeStyle = "tree" | "chart";

export const bornYear = (p: TreePerson) => p.born_year ?? (p.born_on ? Number(p.born_on.slice(0, 4)) : null);
export const diedYear = (p: TreePerson) => p.died_year ?? (p.died_on ? Number(p.died_on.slice(0, 4)) : null);
export const isGone = (p: TreePerson) => !!(p.died_on || p.died_year);
export const fullName = (p: TreePerson) => [p.given_name, p.family_name].filter(Boolean).join(" ");
export const years = (p: TreePerson) => { const b = bornYear(p); const d = diedYear(p); return b && d ? `${b}–${d}` : b ? `Born ${b}` : d ? `Died ${d}` : ""; };

function useInv() {
  const hid = useHouseholdId(); const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: ["tree", hid] });
}

export function useTree() {
  const hid = useHouseholdId();
  return useQuery({
    queryKey: ["tree", hid], enabled: !!hid,
    queryFn: async () => {
      const [p, l, h] = await Promise.all([
        supabase.from("tree_people").select("id,person_id,given_name,family_name,maiden_name,nickname,born_on,born_year,died_on,died_year,birthplace,note,branch_colour").eq("household_id", hid).is("archived_at", null),
        supabase.from("tree_links").select("id,kind,from_id,to_id,status,since_year").eq("household_id", hid).is("archived_at", null),
        supabase.from("households").select("tree_style").eq("id", hid).maybeSingle(),
      ]);
      if (p.error) throw p.error; if (l.error) throw l.error;
      return { people: p.data as TreePerson[], links: l.data as TreeLink[], style: ((h.data as { tree_style?: TreeStyle } | null)?.tree_style ?? "tree") as TreeStyle };
    },
  });
}

export function useSetTreeStyle() {
  const hid = useHouseholdId(); const inv = useInv();
  return useMutation({
    mutationFn: async (s: TreeStyle) => { const { error } = await supabase.from("households").update({ tree_style: s }).eq("id", hid); if (error) throw error; },
    onSuccess: inv,
  });
}

export type PersonDraft = Omit<TreePerson, "id"> & { id?: string };
export type Relation = { kind: "parent" | "partner" | "child" | "sibling"; of: string; status?: PartnerStatus; alsoWith?: string | null };

/** Save someone, and (for a new relative) link them in the right place. Returns their id. */
export function useSaveTreePerson() {
  const hid = useHouseholdId(); const inv = useInv();
  return useMutation({
    mutationFn: async ({ d, rel, existingId, links }: { d?: PersonDraft; rel?: Relation; existingId?: string; links: TreeLink[] }): Promise<string> => {
      let id = existingId;
      if (d) {
        const { id: did, ...row } = d;
        const clean = { ...row, given_name: row.given_name.trim(), family_name: row.family_name?.trim() || null, maiden_name: row.maiden_name?.trim() || null, nickname: row.nickname?.trim() || null, birthplace: row.birthplace?.trim() || null, note: row.note?.trim() || null };
        if (did) { const { error } = await supabase.from("tree_people").update(clean).eq("id", did); if (error) throw error; id = did; }
        else { const { data, error } = await supabase.from("tree_people").insert({ ...clean, household_id: hid }).select("id").single(); if (error) throw error; id = (data as { id: string }).id; }
      }
      if (rel && id) {
        const add = async (kind: "parent" | "partner", from: string, to: string, status: PartnerStatus | null = null) => {
          const { error } = await supabase.from("tree_links").insert({ household_id: hid, kind, from_id: from, to_id: to, status });
          if (error && error.code !== "23505") throw new Error(error.message.includes("two parents") ? "They already have two parents in the tree." : error.message.includes("can't also be") ? "That would make someone their own grandparent." : error.message);
        };
        if (rel.kind === "parent") {
          await add("parent", id, rel.of);
          if (rel.alsoWith) await add("partner", id, rel.alsoWith, "married");
        } else if (rel.kind === "child") {
          await add("parent", rel.of, id);
          if (rel.alsoWith) await add("parent", rel.alsoWith, id);
        } else if (rel.kind === "partner") {
          await add("partner", rel.of, id, rel.status ?? "married");
        } else if (rel.kind === "sibling") {
          const parents = links.filter((l) => l.kind === "parent" && l.to_id === rel.of).map((l) => l.from_id);
          if (!parents.length) throw new Error("Add a parent first, then brothers and sisters hang off them.");
          for (const p of parents) await add("parent", p, id);
        }
      }
      return id!;
    },
    onSuccess: inv,
  });
}

/** Pick the colour a founding branch is drawn in (038). */
export function useSetBranchColour() {
  const inv = useInv();
  return useMutation({
    mutationFn: async ({ id, colour }: { id: string; colour: number | null }) => {
      const { error } = await supabase.from("tree_people").update({ branch_colour: colour }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: inv,
  });
}

export function useUpdateLink() {
  const inv = useInv();
  return useMutation({
    mutationFn: async (a: { id: string; status?: PartnerStatus; archive?: boolean }) => {
      const { error } = await supabase.from("tree_links").update(a.archive ? { archived_at: new Date().toISOString() } : { status: a.status }).eq("id", a.id);
      if (error) throw error;
    },
    onSuccess: inv,
  });
}

/** Take someone out of the tree (they and their lines are archived). */
export function useRemoveTreePerson() {
  const inv = useInv();
  return useMutation({
    mutationFn: async ({ id, links }: { id: string; links: TreeLink[] }) => {
      const at = new Date().toISOString();
      const mine = links.filter((l) => l.from_id === id || l.to_id === id).map((l) => l.id);
      if (mine.length) { const { error } = await supabase.from("tree_links").update({ archived_at: at }).in("id", mine); if (error) throw error; }
      const { error } = await supabase.from("tree_people").update({ archived_at: at }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: inv,
  });
}

/** First visit: put the household's own people in, two adults as partners and children under both. */
export function useStartTree() {
  const hid = useHouseholdId(); const people = usePeople(); const inv = useInv();
  return useMutation({
    mutationFn: async () => {
      const ppl = people.data ?? [];
      const ids = new Map<string, string>();
      for (const p of ppl) {
        const { data, error } = await supabase.from("tree_people").insert({ household_id: hid, person_id: p.id, given_name: p.display_name, born_on: p.born_on ?? null }).select("id").single();
        if (error) throw error;
        ids.set(p.id, (data as { id: string }).id);
      }
      const adults = ppl.filter((p) => p.role === "adult"); const kids = ppl.filter((p) => p.role === "child");
      const links: { household_id: string | undefined; kind: string; from_id: string; to_id: string; status?: string }[] = [];
      if (adults.length === 2) links.push({ household_id: hid, kind: "partner", from_id: ids.get(adults[0].id)!, to_id: ids.get(adults[1].id)!, status: "married" });
      if (adults.length <= 2) for (const k of kids) for (const a of adults) links.push({ household_id: hid, kind: "parent", from_id: ids.get(a.id)!, to_id: ids.get(k.id)! });
      if (links.length) { const { error } = await supabase.from("tree_links").insert(links); if (error) throw error; }
    },
    onSuccess: inv,
  });
}

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------
export type Placed = { id: string; gen: number; x: number };   // x is the centre, in "slots" (1 slot = one person width)
export type Family = { parents: string[]; children: string[] };
export type Layout = { nodes: Map<string, Placed>; gens: number; families: Family[]; partners: TreeLink[]; width: number };

export function layoutTree(people: TreePerson[], links: TreeLink[]): Layout {
  const ids = new Set(people.map((p) => p.id));
  const L = links.filter((l) => ids.has(l.from_id) && ids.has(l.to_id));
  const parentsOf = new Map<string, string[]>(); const childrenOf = new Map<string, string[]>(); const partnersOf = new Map<string, string[]>();
  for (const id of ids) { parentsOf.set(id, []); childrenOf.set(id, []); partnersOf.set(id, []); }
  for (const l of L) {
    if (l.kind === "parent") { parentsOf.get(l.to_id)!.push(l.from_id); childrenOf.get(l.from_id)!.push(l.to_id); }
    else { partnersOf.get(l.from_id)!.push(l.to_id); partnersOf.get(l.to_id)!.push(l.from_id); }
  }
  const by = new Map(people.map((p) => [p.id, p]));
  const birth = (id: string) => bornYear(by.get(id)!) ?? 9999;

  // 1. Generations: child = parent + 1, partners share a row; parents pulled down next to their children.
  const gen = new Map<string, number>([...ids].map((id) => [id, 0]));
  for (let round = 0; round < ids.size * 4 + 10; round++) {
    let changed = false;
    for (const l of L) {
      if (l.kind === "parent" && gen.get(l.to_id)! < gen.get(l.from_id)! + 1) { gen.set(l.to_id, gen.get(l.from_id)! + 1); changed = true; }
      if (l.kind === "partner") { const m = Math.max(gen.get(l.from_id)!, gen.get(l.to_id)!); if (gen.get(l.from_id) !== m || gen.get(l.to_id) !== m) { gen.set(l.from_id, m); gen.set(l.to_id, m); changed = true; } }
    }
    for (const id of ids) {
      const kids = childrenOf.get(id)!; if (!kids.length) continue;
      const target = Math.min(...kids.map((k) => gen.get(k)!)) - 1;
      const ps = parentsOf.get(id)!;
      if (target > gen.get(id)! && ps.every((p) => gen.get(p)! < target)) { gen.set(id, target); changed = true; }
    }
    if (!changed) break;
  }
  const minG = Math.min(...[...gen.values()], 0); for (const [k, v] of gen) gen.set(k, v - minG);
  const gens = Math.max(0, ...gen.values()) + 1;

  // 2. Units: partners in the same row stay side by side.
  const unitOf = new Map<string, string[]>();
  for (const id of ids) {
    if (unitOf.has(id)) continue;
    const unit: string[] = []; const stack = [id];
    while (stack.length) { const x = stack.pop()!; if (unitOf.has(x) || gen.get(x) !== gen.get(id)) continue; unitOf.set(x, unit); unit.push(x); for (const p of partnersOf.get(x)!) stack.push(p); }
    // The one with parents in the tree goes on the outside nearest their family; keep a stable order otherwise.
    unit.sort((a, b) => (parentsOf.get(b)!.length - parentsOf.get(a)!.length) || birth(a) - birth(b));
  }
  const rows: string[][][] = Array.from({ length: gens }, () => []);
  for (const u of new Set(unitOf.values())) rows[gen.get(u[0])!].push(u);

  const nodes = new Map<string, Placed>();
  const GAP = 0.6;   // between units, in slots
  const place = (row: string[][], desired: Map<string[], number>) => {
    // Keep order, honour desired centres where possible, never overlap.
    let cursor = -Infinity;
    for (const u of row) {
      const w = u.length; const want = (desired.get(u) ?? 0) - (w - 1) / 2;
      const left = Math.max(want, cursor);
      u.forEach((id, i) => nodes.set(id, { id, gen: gen.get(id)!, x: left + i }));
      cursor = left + w + GAP;
    }
  };
  const centre = (u: string[]) => u.reduce((t, id) => t + nodes.get(id)!.x, 0) / u.length;
  const meanOf = (xs: string[]) => xs.length ? xs.reduce((t, id) => t + (nodes.get(id)?.x ?? 0), 0) / xs.length : null;

  // 3. First placement, top row by birth, each lower row by where its parents sit.
  rows[0].sort((a, b) => Math.min(...a.map(birth)) - Math.min(...b.map(birth)));
  { let c = 0; const d = new Map<string[], number>(); for (const u of rows[0]) { d.set(u, c + (u.length - 1) / 2); c += u.length + GAP; } place(rows[0], d); }
  for (let g = 1; g < gens; g++) {
    const key = (u: string[]) => meanOf(u.flatMap((id) => parentsOf.get(id)!)) ?? Infinity;
    rows[g].sort((a, b) => key(a) - key(b) || Math.min(...a.map(birth)) - Math.min(...b.map(birth)));
    const d = new Map<string[], number>(); let c = 0;
    for (const u of rows[g]) { const k = key(u); d.set(u, Number.isFinite(k) ? k : c); c = (Number.isFinite(k) ? k : c) + u.length + GAP; }
    place(rows[g], d);
  }
  // 4. A few passes: parents over their children, then children under their parents.
  for (let pass = 0; pass < 4; pass++) {
    for (let g = gens - 2; g >= 0; g--) {
      const d = new Map<string[], number>();
      for (const u of rows[g]) { const kids = [...new Set(u.flatMap((id) => childrenOf.get(id)!))]; const m = meanOf(kids); d.set(u, m ?? centre(u)); }
      rows[g].sort((a, b) => d.get(a)! - d.get(b)!);
      place(rows[g], d);
    }
    for (let g = 1; g < gens; g++) {
      const d = new Map<string[], number>();
      for (const u of rows[g]) {
        // Inside a couple, each sits on the side their own parents are on, so lines don't cross.
        if (u.length > 1) {
          const c0 = centre(u);
          const side = (id: string) => { const m = meanOf(parentsOf.get(id)!); return m === null ? 0 : m - c0; };
          u.sort((a, b) => side(a) - side(b) || birth(a) - birth(b));
        }
        const m = meanOf(u.flatMap((id) => parentsOf.get(id)!)); d.set(u, m ?? centre(u));
      }
      rows[g].sort((a, b) => d.get(a)! - d.get(b)!);
      place(rows[g], d);
    }
  }
  const minX = nodes.size ? Math.min(...[...nodes.values()].map((n) => n.x)) : 0;
  for (const n of nodes.values()) n.x -= minX;
  const width = Math.max(1, ...[...nodes.values()].map((n) => n.x + 1));

  // Families: each set of parents with their children.
  const fam = new Map<string, Family>();
  for (const id of ids) {
    const ps = [...parentsOf.get(id)!].sort(); if (!ps.length) continue;
    const k = ps.join("+"); const f = fam.get(k) ?? { parents: ps, children: [] };
    f.children.push(id); fam.set(k, f);
  }
  for (const f of fam.values()) f.children.sort((a, b) => nodes.get(a)!.x - nodes.get(b)!.x);
  return { nodes, gens, families: [...fam.values()], partners: L.filter((l) => l.kind === "partner"), width };
}
